import React, { useEffect, useRef } from "react";
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineController,
  PointElement, // ✅ เพิ่มอันนี้
  LineElement,   // ✅ และเพิ่มอันนี้ด้วย (สำคัญสำหรับเส้น)
} from "chart.js";
import { useTranslation } from "react-i18next";

import { Card, CardContent, Typography, Box } from "@mui/material";


interface SocialMediaData {
  month: number;
  count_num_line: number;
  percent_line: number;
  count_num_facebook: number;
  percent_facebook: number;
  count_num_x: number;
  percent_x: number;
}

interface LineChartProps {
  data: SocialMediaData[];
  title?: string;
  type?: "count" | "percent";
  platforms?: ("line" | "facebook" | "x")[];
  maxValue?: number;
}

Chart.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineController,
  PointElement,
  LineElement
);

const SocialMediaLineChart: React.FC<LineChartProps> = ({
  data,
  title,
  type = "count",
  platforms = ["line", "facebook", "x"],
  maxValue,
}) => {
  const { t } = useTranslation();
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  // Month names in Thai
  const monthNames = [
    t("ม.ค."),
    t("ก.พ."),
    t("มี.ค."),
    t("เม.ย."),
    t("พ.ค."),
    t("มิ.ย."),
    t("ก.ค."),
    t("ส.ค."),
    t("ก.ย."),
    t("ต.ค."),
    t("พ.ย."),
    t("ธ.ค."),
  ];

  // Platform configuration
  const platformConfig = {
    line: {
      label: "LINE",
      borderColor: "#00C851",
      backgroundColor: "#00C851",
      borderDash: [8, 4],
      tension: 0.5, // More curved line for LINE
    },
    facebook: {
      label: "Facebook",
      borderColor: "#1877F2",
      backgroundColor: "#1877F2",
      borderDash: [8, 4],
      tension: 0.5, // More curved line for LINE
    },
    x: {
      label: "X",
      borderColor: "#000000",
      backgroundColor: "#000000",
      borderDash: [8, 4],
      tension: 0.5, // More curved line for LINE
    },
  };

  // Calculate dynamic max value if not provided
  const calculateMaxValue = () => {
    if (maxValue) return maxValue;

    let max = 0;
    data.forEach((item) => {
      platforms.forEach((platform) => {
        const value =
          type === "count"
            ? (item[`count_num_${platform}` as keyof SocialMediaData] as number)
            : (item[`percent_${platform}` as keyof SocialMediaData] as number);
        max = Math.max(max, value);
      });
    });

    // Add some padding to the max value
    if (type === "percent") {
      return Math.min(100, max + 10);
    } else {
      return max === 0 ? 10 : Math.ceil(max * 1.2);
    }
  };

  useEffect(() => {
    if (chartRef.current) {
      const ctx = chartRef.current.getContext("2d");

      if (ctx) {
        if (chartInstance.current) {
          chartInstance.current.destroy();
        }

        // Create datasets based on selected platforms
        const datasets = platforms.map((platform) => ({
          label: platformConfig[platform].label,
          data: data.map((item) => {
            if (type === "count") {
              return item[
                `count_num_${platform}` as keyof SocialMediaData
              ] as number;
            } else {
              return item[
                `percent_${platform}` as keyof SocialMediaData
              ] as number;
            }
          }),
          borderColor: platformConfig[platform].borderColor,
          backgroundColor: platformConfig[platform].backgroundColor,
          fill: false,
          tension: platformConfig[platform].tension,
          pointRadius: 6,
          pointHoverRadius: 8,
          borderDash: platformConfig[platform].borderDash,
          borderWidth: 2, // Make lines a bit thicker for better visibility
        }));

        const dynamicMaxValue = calculateMaxValue();

        chartInstance.current = new Chart(ctx, {
          type: "line",
          data: {
            labels: data.map((item) => monthNames[item.month - 1]),
            datasets,
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              title: {
                display: false,
              },
              legend: {
                display: true,
                position: "bottom",
                labels: {
                  usePointStyle: true,
                  padding: 15,
                  font: {
                    size: 12,
                  },
                },
              },
              tooltip: {
                mode: "index",
                intersect: false,
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                titleColor: "#fff",
                bodyColor: "#fff",
                borderColor: "rgba(255, 255, 255, 0.1)",
                borderWidth: 1,
                callbacks: {
                  label: function (context) {
                    const value = context.parsed.y;
                    if (type === "count") {
                      return t("{{label}}: {{value}} รายการ", { label: context.dataset.label, value: value.toLocaleString() }) as string;
                    } else {
                      return `${context.dataset.label}: ${value.toFixed(1)}%`;
                    }
                  },
                },
              },
            },
            scales: {
              x: {
                grid: { display: false },
                ticks: {
                  font: { size: 11 },
                },
              },
              y: {
                beginAtZero: true,
                max: dynamicMaxValue,
                grid: {
                  color: "rgba(0, 0, 0, 0.1)",
                },
                ticks: {
                  stepSize:
                    type === "percent"
                      ? 10
                      : Math.max(1, Math.floor(dynamicMaxValue / 10)),
                  font: { size: 11 },
                  callback: function (value: string | number) {
                    if (type === "count") {
                      return Number(value).toLocaleString();
                    } else {
                      return `${value}%`;
                    }
                  },
                },
              },
            },
            interaction: {
              mode: "nearest",
              axis: "x",
              intersect: false,
            },
            elements: {
              point: {
                hoverBorderWidth: 3,
              },
            },
            layout: {
              padding: {
                top: 10,
                bottom: 10,
              },
            },
          },
        });
      }
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data, type, platforms, maxValue, t]);

  return (
    <Card
      sx={{
        width: "100%",
        minHeight: 450,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          p: 3,
          display: "flex",
          flexDirection: "column",
          "&:last-child": { pb: 3 },
        }}
      >
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontWeight: 600,
            mb: 2,
            color: "text.primary",
            flexShrink: 0,
          }}
        >
          {title ?? t("จำนวนการแชร์ (แยกตามประเภทโซเชียลมีเดีย)")}
        </Typography>

        <Box
          sx={{
            position: "relative",
            flex: 1,
            minHeight: 350,
            width: "100%",
          }}
        >
          <canvas ref={chartRef} />
        </Box>
      </CardContent>
    </Card>
  );
};

export default SocialMediaLineChart;